// @flow

import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";

import config from "@config";
import http from "@http";
import actionCreators from "@modules/service/actionCreator";
import { transformFilters, OPERATORS } from "@transformers/request";
import { sortToString, transformSort } from "@transformers/sort";
import type {
  SpecialContact,
  SpecialContactEmployeeSearch,
} from "@modules/service/reducer/special-contacts/types";
import {
  GetSpecialContacts,
  GetSpecialContactsEmployees,
  AddSpecialContact,
  UpdateSpecialContact,
  RemoveSpecialContact,
} from "@modules/service/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";

type ListResponse<T> = $Exact<{
  content: T,
  paginations: any,
}>;

type GetSpecialContactsRequest = $Exact<{
  clientId: string,
  page: number,
  search: Object,
  sort: Object,
}>;

export function* onGetSpecialContacts({
  payload,
}: Action<GetSpecialContactsRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<SpecialContact>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/special-contact`,
      {
        params: {
          page: payload.page,
          limit: config.shortResultsPerPage,
          sort: sortToString(transformSort(payload.sort)),
          search: transformFilters(payload.search || {}, {
            type_id: OPERATORS.EQUALS,
          }),
        },
      },
    );

    yield all([
      put(actionCreators.SetSpecialContactsListOptions(payload)),
      put(actionCreators.GetSpecialContactsSuccess(response.data)),
    ]);
  } catch (e) {
    yield put(actionCreators.GetSpecialContactsError(e.response));
  }
}

type GetSpecialContactsEmployeesRequest = $Exact<{
  clientId: string,
  page: number,
  search: Object,
}>;

export function* onGetSpecialContactsEmployees({
  payload,
}: Action<GetSpecialContactsEmployeesRequest>): Generator<
  *,
  void,
  { data: ListResponse<Array<SpecialContactEmployeeSearch>> },
> {
  try {
    if (!payload || !payload.clientId) {
      throw Error("Invalid payload provided");
    }

    const response = yield call(
      http.get,
      `/api/v1/service-admin/client/${payload.clientId}/special-contact/employee-search`,
      {
        params: {
          page: payload.page,
          limit: config.shortResultsPerPage,
          search: transformFilters(payload.search || {}, {
            last_name: OPERATORS.STARTS_WITH,
          }),
        },
      },
    );

    yield put(actionCreators.GetSpecialContactsEmployeesSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetSpecialContactsEmployeesError(e.response));
  }
}

type AddSpecialContactsRequest = $Exact<{
  clientId: number,
  data: Object,
}>;

export function* onAddSpecialContact({
  payload,
}: Action<AddSpecialContactsRequest>): Generator<*, void, SpecialContact> {
  try {
    if (!payload || !payload.clientId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.post,
      `/api/v1/service-admin/client/${payload.clientId}/special-contact`,
      payload.data,
    );

    yield all([
      put(actionCreators.AddSpecialContactSuccess()),
      put(actionCreators.SetShowAddSpecialContactModal(false)),
      put(sendSuccessMessage("SPECIAL_CONTACT_CREATED")),
      put(
        actionCreators.GetSpecialContacts({
          clientId: payload.clientId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.AddSpecialContactError(e.response));
  }
}

type UpdateSpecialContactsRequest = $Exact<{
  clientId: number,
  specialContactId: number,
  data: Object,
}>;

export function* onUpdateSpecialContact({
  payload,
}: Action<UpdateSpecialContactsRequest>): Generator<*, void, SpecialContact> {
  try {
    if (!payload || !payload.clientId || !payload.specialContactId || !payload.data) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.put,
      `/api/v1/service-admin/client/${payload.clientId}/special-contact/${payload.specialContactId}`,
      payload.data,
    );

    yield all([
      put(actionCreators.UpdateSpecialContactSuccess()),
      put(actionCreators.SetShowEditSpecialContactModal(false)),
      put(sendSuccessMessage("SPECIAL_CONTACT_UPDATED")),
      put(
        actionCreators.GetSpecialContacts({
          clientId: payload.clientId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.UpdateSpecialContactError(e.response));
  }
}

type RemoveSpecialContactsRequest = $Exact<{
  clientId: number,
  specialContactId: number,
}>;

export function* onRemoveSpecialContact({
  payload,
}: Action<RemoveSpecialContactsRequest>): Generator<*, void, SpecialContact> {
  try {
    if (!payload || !payload.clientId || !payload.specialContactId) {
      throw Error("Invalid payload provided");
    }

    yield call(
      http.delete,
      `/api/v1/service-admin/client/${payload.clientId}/special-contact/${payload.specialContactId}`,
    );

    yield all([
      put(actionCreators.RemoveSpecialContactSuccess()),
      put(actionCreators.SetShowDeleteSpecialContactModal(false)),
      put(sendSuccessMessage("SPECIAL_CONTACT_REMOVED")),
      put(
        actionCreators.GetSpecialContacts({
          clientId: payload.clientId,
        }),
      ),
    ]);
  } catch (e) {
    yield put(actionCreators.RemoveSpecialContactError(e.response));
  }
}

export function* SpecialContactSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetSpecialContacts, onGetSpecialContacts),
    takeEvery(GetSpecialContactsEmployees, onGetSpecialContactsEmployees),
    takeEvery(AddSpecialContact, onAddSpecialContact),
    takeEvery(UpdateSpecialContact, onUpdateSpecialContact),
    takeEvery(RemoveSpecialContact, onRemoveSpecialContact),
  ]);
}

export default SpecialContactSagas;
